import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table/interface";
import { StatusWithIcon } from "@shared/ui";
import { formatDate, formatMoscowTime } from "@shared/utils";
import { JournalVersion } from "../../model/types";
import { IntegrationStatusTag } from "../integration-status-tag";
import { VersionStatusTag } from "../version-status-tag";
import "./version-table.scss";

const DEFAULT_RESPONSIBLE_TITLE =
  "Представитель, ответственный за внесение изменений";

interface VersionTableProps {
  versions: JournalVersion[];
  onVersionClick?: (version: JournalVersion) => void;
  responsibleColumnTitle?: string;
  versionDateColumnTitle?: string;
  showChangeReasonColumn?: boolean;
  pagination?: false | TablePaginationConfig;
}

const formatDateCell = (dateStr: string) => (
  <div className="version-table__date">
    <span>{formatDate(dateStr)}</span>
    <span className="version-table__date-time">{formatMoscowTime(dateStr)} МСК</span>
  </div>
);

const buildColumns = (
  responsibleColumnTitle: string,
  versionDateColumnTitle: string,
  showChangeReasonColumn: boolean,
): ColumnsType<JournalVersion> => {
  const cols: ColumnsType<JournalVersion> = [
    {
      title: "Версия",
      dataIndex: "versionNumber",
      key: "versionNumber",
      width: 120,
      render: (_, record) => (
        <VersionStatusTag
          status={record.status}
          versionNumber={record.versionNumber}
          variant="table"
        />
      ),
    },
    {
      title: "Статус",
      dataIndex: "integrationStatus",
      key: "integrationStatus",
      width: 220,
      render: (_, record) =>
        record.recordStatus ? (
          <StatusWithIcon status={record.recordStatus} />
        ) : (
          <IntegrationStatusTag status={record.integrationStatus} />
        ),
    },
    {
      title: versionDateColumnTitle,
      dataIndex: "versionDate",
      key: "versionDate",
      width: 200,
      render: (value) => formatDateCell(value),
    },
    {
      title: responsibleColumnTitle,
      dataIndex: "responsible",
      key: "responsible",
      render: (_, record) => {
        if (!record.responsible) return null;
        return (
          <div className="version-table__responsible">
            <span className="version-table__responsible-name">
              {record.responsible.name}
            </span>
            <span className="version-table__responsible-role">
              {record.responsible.role}
            </span>
          </div>
        );
      },
    },
  ];

  if (showChangeReasonColumn) {
    cols.push({
      title: "Причина внесения изменений",
      key: "changeReason",
      width: 220,
      render: (_, record) => record.changeReason ?? "—",
    });
  }

  return cols;
};

export const VersionTable = ({
  versions,
  onVersionClick,
  responsibleColumnTitle = DEFAULT_RESPONSIBLE_TITLE,
  versionDateColumnTitle = "Дата версии",
  showChangeReasonColumn = false,
  pagination = false,
}: VersionTableProps) => {
  const columns = buildColumns(
    responsibleColumnTitle,
    versionDateColumnTitle,
    showChangeReasonColumn,
  );

  return (
    <div className="version-table">
      <Table<JournalVersion>
        columns={columns}
        dataSource={versions}
        rowKey="versionId"
        pagination={pagination}
        onRow={(record) => ({
          onClick: () => onVersionClick?.(record),
          className:
            record.status === "OUTDATED" ? "version-table__row--outdated" : "",
          style: { cursor: onVersionClick ? "pointer" : undefined },
        })}
        className="version-table__table"
      />
    </div>
  );
};
